import { Table } from 'antd';
import { useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';
import { setOpenKeys, setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import useDebouncedValue from '../../../../../../../hooks/use-debonced-value';
import { GetCompaniesSuccessResponse } from '../../../../../types/api/get-companies-success-response';
import { SearchBar } from '../../../../../../../components/search-bar/search-bar';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';

type TableDataType = {
    id: number;
    name: string;
};

type DeleteColumnProps = {
    company: TableDataType;
    revalidate: () => void;
};

const DeleteColumn = ({ company: { id }, revalidate }: DeleteColumnProps) => {
    const dispatch = useDispatch();

    const onClick = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if(window.confirm('Biztosan törlöd a céget?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/companies/${ id }/soft-delete`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 204) {
                revalidate();
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a törlendő cég nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));

        }

    }

    return (
        <a onClick={onClick}>
            <img src={getImageUrl('delete-icon2.png')} alt="delete-icon2" />
        </a>
    );
}

export const AdminActiveCompanyList = () => {
    const dispatch = useDispatch();
    const loaderData: any = useLoaderData();
    const revalidator = useRevalidator();
    useEffect(() => {
        dispatch(setOpenKeys(['admin-companies']));
        dispatch(setSelectedMenuItemKeys(['admin-active-companies']));

        return () => {
            dispatch(setOpenKeys([]));
        };
    }, [dispatch]);

    const [data, setData] = useState<TableDataType[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedValue(searchTerm);

    const handleSearchBarChange = (value: string) => {
        setSearchTerm(value);
        setDebouncedSearchTerm(value);
    };

    useEffect(() => {
        const messageShowed = showDefaultErrorMessages(loaderData.getCompaniesResponse);
        if(messageShowed) return;

        if(loaderData.getCompaniesResponse?.status === 200) {
            const res: AxiosResponse<GetCompaniesSuccessResponse> = loaderData.getCompaniesResponse;
            const tableData: TableDataType[] = [];
            for (const company of res.data.companies) {
                if (company.name.toLocaleLowerCase().includes(debouncedSearchTerm.toLocaleLowerCase().trim()))
                    tableData.push({
                        id: company.id,
                        name: company.name
                    });
            }

            setData(tableData);
            return;
        }

    }, [loaderData, debouncedSearchTerm]);
    
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a: TableDataType, b: TableDataType) => a.id - b.id
        },
        {
            title: 'Név',
            dataIndex: 'name',
            sorter: (a: TableDataType, b: TableDataType) => String(a.name).localeCompare(b.name)
        },
        {
            title: 'Adatlap',
            render: (record: TableDataType) => {
                return (
                    <Link to={`/cms/admin/companies/edit/${record.id}`}>
                        <img src={getImageUrl('edit-icon.png')} alt="edit-icon" />
                    </Link>
                );
            }
        },
        {
            title: 'Törlés',
            render: (record: TableDataType) => {
                return (
                    <DeleteColumn company={record} revalidate={revalidator.revalidate}/>
                );
            }
        }
    ];

    return (
        <>
            <div className="page-title">Aktív cégek</div>
            <div className='d-flex justify-content-between gap-3 mb-4'>
                <SearchBar value={searchTerm} onChange={handleSearchBarChange} />
                <Link to='/cms/admin/companies/add'>
                    <CMSButton text='Új cég hozzáadása' iconSrc={getImageUrl('add-company.png')} />
                </Link>
            </div>
            <Table
                className='table-responsive data-table'
                columns={columns}
                dataSource={data}
                bordered
                pagination={
                    {
                        position: ['bottomRight'],
                        pageSize: 10
                    }
                }
            />
        </>
    );
}